import { useEffect, useState } from "react";
import API from "../api";
import { boxStyle, dataGridStyle, logColor } from "../theme";
import { IconButton, Box, Typography, Grid, Dialog, DialogTitle, DialogContent, Table, TableCell, TableBody, TableRow, TableHead, Tooltip, Button, Chip } from "@mui/material";
import TuneIcon from '@mui/icons-material/Tune';
import PanToolIcon from '@mui/icons-material/PanTool';
import { DataGridPro } from "@mui/x-data-grid-pro";
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from "dayjs";
import ChooseIpDialog from "./ChooseIpSuppDialog";
import { startToast, updateToastError, updateToastSuccess } from "../utils/notification-loading";

export const SuppressionSetting = ({ tipo, customRules = [] }) => {
  const ctrl = new AbortController();
  const { signal } = ctrl;
  const [rows, setRows] = useState([]);
  const [suppDialog, setSuppDialog] = useState(false);
  const [addRuleDialog, setAddRuleDialog] = useState(false);
  const [selectedRow, setSelectedRow] = useState({dettaglio: "", id_dettaglio: "", tipo: -1});
  const [loading, setLoading] = useState(true);
  const style = {
    datagrid: {
      ...dataGridStyle,
      height: "47vh",
    },
    title: {
      fontSize: 16,
      fontWeight: 700,
      textAlign: "left",
      mb: 1.5,
    }
  }

  const customRuleNames = new Set((customRules ?? []).map(r => r.ruleName).filter(Boolean));

  useEffect(() => {
    setLoading(true);
    API.getRaises({tipo: tipo}).then((res)=> {
      setRows(res.data ?? []);
      setLoading(false);
    }).catch((err) => {
      console.log(err)
    });
  }, [tipo]);

  const handleDetailClick = (row) => {
    setSuppDialog(true);
    setSelectedRow(row);
  }

  function changeCount(dettaglio, operator) {
    const delta = operator === "+" ? 1 : operator === "-" ? -1 : 0;
    if (delta === 0) return;

    setRows(prev =>
      prev.map(r =>
        r.dettaglio === dettaglio
          ? { ...r, count: Math.max(0, (r.count ?? 0) + delta) }
          : r
      )
    );
  }

  const handleAddRule = (srcIp, destIp) => {
    startToast();
    API.manageSuppression({
      action: "add",
      src_ip: srcIp,
      dest_ip: destIp,
      dettaglio: selectedRow.dettaglio || "",
      severity: selectedRow.severity
    }, signal).then((res) => {
      setSelectedRow(prev => {
        prev.config.push({id: res.data?.id, src_ip: srcIp, dest_ip: destIp, timeStamp: new Date()})
        return {...prev}
      });
      changeCount(selectedRow.dettaglio, "+");
      setAddRuleDialog(false);
      updateToastSuccess("Suppression rule added");
    }).catch((err) => {
      updateToastError("Could not add the rule");
    })
  }

  const handleRemoveRule = (index) => {
    startToast();
    const item = selectedRow['config'][index];
    API.manageSuppression({
      action: "del",
      src_ip: item.src_ip,
      dest_ip: item.dest_ip,
      dettaglio: selectedRow.dettaglio || "",
      severity: selectedRow.severity
    }, signal).then((res) => {
      setSelectedRow((prev) => ({...prev, config: prev.config.filter((_, i) => i !== index)}));
      changeCount(selectedRow.dettaglio, "-");
      updateToastSuccess("Suppression rule removed");
    }).catch((err) => {
      updateToastError("Could not remove the rule");
    })
  };

  return (
    <>
      <ChooseIpDialog
        open={addRuleDialog}
        setOpen={setAddRuleDialog}
        handleAddRule={handleAddRule}
      />
      <SuppDialog
        open={suppDialog}
        setOpen={setSuppDialog}
        row={selectedRow}
        setAddRuleDialog={setAddRuleDialog}
        handleRemoveRule={handleRemoveRule}
      />
      <Box sx={{
        backgroundColor: 'rgba(69, 144, 149, 0.05)',
        borderRadius: 2,
        border: '1px solid rgba(69, 144, 149, 0.1)',
        p: 2
      }}>
        <Grid container spacing={2}>
          <Grid size={12}>
          </Grid>
          {Array.isArray(tipo) ? (
            <>
              <Grid size={6}>
                <Typography sx={{...style.title, color: logColor[1]}}>Critical logs</Typography>
                <DataGridPro
                  rows={(rows ?? []).filter((item) => item.severity === 1 && !customRuleNames.has(item.dettaglio))}
                  columns={mainTableColumn(handleDetailClick)}
                  loading={loading}
                  sx={{...style.datagrid}}
                />
              </Grid>
              <Grid size={6}>
                <Typography sx={{...style.title, color: logColor[0]}}>High logs</Typography>
                <DataGridPro
                  rows={(rows ?? []).filter((item) => item.severity === 0 && !customRuleNames.has(item.dettaglio))}
                  columns={mainTableColumn(handleDetailClick)}
                  loading={loading}
                  sx={{...style.datagrid}}
                />
              </Grid>
            </>
          ):(
            <Grid size={12}>
              <Typography sx={{...style.title, color: logColor[tipo]}}>{tipo === 1 ? "Critical logs" : "High logs"}</Typography>
              <DataGridPro
                rows={rows ?? []}
                columns={mainTableColumn(handleDetailClick)}
                  loading={loading}
                sx={{...style.datagrid}}
              />
            </Grid>
          )}
        </Grid>
      </Box>
    </>
  )
};

const mainTableColumn = (handleDetailClick) => [
  {
    field: "manage",
    headerName: "",
    width: 70,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    headerAlign: "center",
    align: "center",
    renderCell: (params) => (
      <Tooltip title="Manage suppression rules">
        <IconButton size="small" onClick={() => handleDetailClick(params.row)}>
          <TuneIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    )
  },
  {
    field: "dettaglio",
    headerName: "LOG",
    width: 400,
    flex: 1,
    type: "string"
  },
  {
    field: "config",
    headerName: "RULES",
    width: 100,
    headerAlign: "center",
    align: "center",
    renderCell: (params) => {
      const n = params?.value?.length ?? 0;
      return (
        <Chip
          label={n}
          size="small"
          sx={{
            minWidth: 32, fontWeight: 700,
            color: n > 0 ? "#449095" : "#666",
            backgroundColor: n > 0 ? "rgba(69,144,149,0.14)" : "rgba(255,255,255,0.05)",
          }}
        />
      );
    }
  }
]

const SuppDialog = ({open, setOpen, row, setAddRuleDialog, handleRemoveRule}) => {
  const rules = row?.config ?? [];
  const headCell = { fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", color: "#777", borderBottom: "1px solid #252525", py: 1 };
  const cell = { borderBottom: "1px solid #1e1e1e", fontSize: 13, color: "#eaeaea" };
  const ipChip = (value) => (
    <Chip label={value || "ANY"} size="small" variant="outlined"
      sx={{ fontFamily: "monospace", borderColor: "rgba(69,144,149,0.5)", color: "#eaeaea" }} />
  );

  return (
    <Dialog
      maxWidth="md"
      fullWidth
      open={open}
      onClose={() => setOpen(false)}
      slotProps={{ paper: { sx: { backgroundColor: "#141414", backgroundImage: "none", border: "1px solid #252525", borderRadius: 2.5 } } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, borderBottom: "1px solid #1e1e1e", py: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: "0.68rem", fontWeight: 800, letterSpacing: "0.12em", color: "#449095", mb: 0.5 }}>
            SUPPRESSION RULES
          </Typography>
          <Typography sx={{ fontSize: 16, fontWeight: 700, wordBreak: "break-word" }}>{row?.dettaglio}</Typography>
          <Typography sx={{ fontSize: 12, color: "#888", mt: 0.5 }}>
            {rules.length === 0 ? "No active rules" : `${rules.length} active ${rules.length === 1 ? "rule" : "rules"}`}
          </Typography>
        </Box>
        <Button
          size="small"
          variant="contained"
          startIcon={<PlaylistAddIcon fontSize="small" />}
          onClick={() => setAddRuleDialog(true)}
          sx={{ flexShrink: 0, backgroundColor: "#449095", "&:hover": { backgroundColor: "#367a7e" } }}
        >
          Add rule
        </Button>
      </DialogTitle>

      <DialogContent sx={{ p: 0, minHeight: 220 }}>
        {rules.length > 0 ? (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headCell}>SOURCE</TableCell>
                <TableCell sx={headCell}>DESTINATION</TableCell>
                <TableCell sx={headCell}>ADDED</TableCell>
                <TableCell sx={headCell}>ID</TableCell>
                <TableCell sx={headCell} />
              </TableRow>
            </TableHead>
            <TableBody>
              {rules.map((item, index) => {
                const added = item.timeStamp ?? item.timestamp;
                return (
                  <TableRow key={item.id ?? index} sx={{ "&:hover": { backgroundColor: "rgba(69,144,149,0.06)" } }}>
                    <TableCell sx={cell}>{ipChip(item.src_ip)}</TableCell>
                    <TableCell sx={cell}>{ipChip(item.dest_ip)}</TableCell>
                    <TableCell sx={{ ...cell, color: "#aaa", fontSize: 12 }}>
                      {added ? dayjs(added).format("YYYY-MM-DD HH:mm") : "-"}
                    </TableCell>
                    <TableCell sx={{ ...cell, maxWidth: 160 }}>
                      {item.id && (
                        <Tooltip title="Click to copy" placement="top">
                          <Typography
                            onClick={() => { navigator.clipboard.writeText(item.id); updateToastSuccess("Rule ID copied"); }}
                            sx={{
                              fontSize: "0.72rem", color: "#999", fontFamily: "monospace",
                              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                              cursor: "pointer", "&:hover": { color: "#449095" }
                            }}
                          >
                            {item.id}
                          </Typography>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ ...cell, pr: 1 }}>
                      <Tooltip title="Remove rule">
                        <IconButton size="small" color="error" onClick={() => handleRemoveRule(index)}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1, height: 220, textAlign: "center", px: 3 }}>
            <PanToolIcon sx={{ fontSize: 30, color: "#333" }} />
            <Typography sx={{ fontSize: 15, fontWeight: 600, color: "#777" }}>No suppression rules for this log</Typography>
            <Typography sx={{ fontSize: 12.5, color: "#555", maxWidth: 380 }}>
              The automatic response runs for every host. Add a rule to skip it for a specific source and destination.
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  )
}

